import arraySort from 'array-sort'
import clone from 'clone'
import moment from 'moment'
import { isPast } from 'date-fns'
import {
  GERMANY_CONTRACT_ADDENDUM_TYPES,
  GERMANY_CONTRACT_STATUSES,
  MISSION_STATUS_ENUM,
} from '../../../../shared/constants'
import { COUNTRY_CODE, PAYROLL_PROVIDER_TYPES } from '../../../../constants'
/**
 * Sort
 *
 * @param type
 * @param data
 * @returns {Array}
 */
export const sort = ({ data, sortBy }) => {
  let dataNew = clone(data)

  if (sortBy.columnName) {
    switch (sortBy.columnName) {
      case 'client':
        arraySort(dataNew, sortByText(sortBy, 'client'))
        break

      case 'type':
        arraySort(dataNew, sortByText(sortBy, 'type'))
        break

      case 'quantity':
        arraySort(dataNew, sortByQuantity(sortBy, 'quantity'))
        break

      case 'timeStart':
        arraySort(dataNew, sortByDate(sortBy, 'timeStart'))
        break

      case 'location':
        arraySort(dataNew, sortByText(sortBy, 'location'))
        break

      case 'street':
        arraySort(dataNew, sortByText(sortBy, 'street'))
        break

      default:
        arraySort(dataNew, sortBy.columnName, { reverse: sortBy.reverse })
    }
  }

  return dataNew
}

/**
 * Sort activities by name.
 *
 * @returns {function(*, *)}
 */
const sortByText = (sortBy, field) => {
  const reverse = sortBy.reverse
  let aText
  let bText

  return (a, b) => {
    switch (field) {
      case 'location':
        aText = a['location']['city']['name']
        bText = b['location']['city']['name']
        break
      case 'street':
        aText = a['location']['name']
        bText = b['location']['name']
        break
      case 'client':
        aText = a['client']['name']
        bText = b['client']['name']
        break
      case 'type':
        aText = a['type']['name']
        bText = b['type']['name']
        break
      default:
        aText = a['client']['name']
        bText = b['client']['name']
        break
    }

    return reverse ? bText.localeCompare(aText) : aText.localeCompare(bText)
  }
}

/**
 * Sort by date.
 *
 * @returns {function(*, *)}
 */
const sortByDate = (sortBy, field) => {
  const reverse = sortBy.reverse
  let aDate
  let bDate

  return (a, b) => {
    switch (field) {
      case 'timeStart':
        aDate = new Date(a['timeStart'])
        bDate = new Date(b['timeStart'])
        break
      default:
        aDate = new Date(a['timeStart'])
        bDate = new Date(b['timeStart'])
        break
    }
    return reverse ? bDate - aDate : aDate - bDate
  }
}

/**
 * Sort by quantity.
 *
 * @returns {function(*, *)}
 */
const sortByQuantity = (sortBy, field) => {
  const reverse = sortBy.reverse
  let quantityA
  let quantityB

  return (a, b) => {
    switch (field) {
      case 'quantity':
        quantityA = a.quantity
        quantityB = b.quantity
        break
      default:
        quantityA = a.quantity
        quantityB = b.quantity
        break
    }

    return reverse ? quantityB - quantityA : quantityA - quantityB
  }
}

export const getLastMissionDateLabel = missions => {
  let date = null
  missions.forEach(mission => {
    if (
      mission.status === MISSION_STATUS_ENUM.COMPLETED ||
      mission.status === MISSION_STATUS_ENUM.PAID
    ) {
      if (new Date(mission.dateTime) > new Date(date)) {
        date = mission.dateTime
      }
    }
  })
  return date ? moment(date).format('DD MMMM YYYY') : 'N/A'
}

export const getPayrollProviderData = ({
  ambassador = {},
  userContract = {},
}) => {
  let status = 'warning'
  let label = ''
  let payrollProviderName = '-'
  let showStatus = true

  if (ambassador.status === 'active' && userContract === null) {
    status = 'danger'
    label = 'Contract required'
  }

  if (userContract) {
    status = 'success'
    label = 'Contract active'
    if (isPast(userContract.end_at)) {
      status = 'warning'
      label = 'Contract expired'
    }
  } else if (ambassador.status !== 'active') {
    showStatus = false
  }

  if (ambassador.payrollProvider === 'NL_TENTOO') {
    payrollProviderName = 'Tentoo'
  }
  if (ambassador.payrollProvider === 'NL_NMBRS') {
    payrollProviderName = 'NMBRS'
  }
  return { label, status, payrollProviderName, showStatus }
}

export const getTemplateDocumentName = ambassador => {
  switch (ambassador.payrollProvider) {
    case PAYROLL_PROVIDER_TYPES.NL_TENTOO:
      return ''
    case PAYROLL_PROVIDER_TYPES.NL_NMBRS:
      return 'NL_NMBRS_6_MONTH_CONTRACT'
  }
}
const getAddendumStatus = add => {
  if (add.signedAt) {
    return GERMANY_CONTRACT_STATUSES.STATUS_SIGNED
  } else {
    return 'PENDING'
  }
}
const decorateAddsWithNewValue = adds => {
  const decoratedAddendums = adds
    ? adds
        .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))
        .map((add, index) => ({
          ...add,
          status: getAddendumStatus(add),
          metadata: {
            ...add.metadata,
            serialNumber: `${adds.length - index}`,
          },
        }))
    : []
  return decoratedAddendums
}

export const getContractStatus = (startDate, endDate) => {
  const currentDate = moment()
  const startDateTime = moment(startDate)
  const endDateTime = moment(endDate, 'YYYY-MM-DD')
    .hours(23)
    .minutes(59)
    .seconds(59)

  const diffInWeeks = endDateTime.diff(currentDate, 'weeks')

  if (diffInWeeks <= 3 && currentDate.isSameOrBefore(endDateTime)) {
    return GERMANY_CONTRACT_STATUSES.STATUS_EXPIRING_SOON
  }

  if (currentDate.isAfter(endDate)) {
    return GERMANY_CONTRACT_STATUSES.STATUS_TERMINATED
  }
  if (startDateTime <= currentDate) {
    return GERMANY_CONTRACT_STATUSES.STATUS_ACTIVE
  } else {
    return GERMANY_CONTRACT_STATUSES.STATUS_SIGNED
  }
}

const decorateContractWithNewValues = ({ contract, addendums }) => {
  const hoursPerWeekAddendums = addendums.filter(
    add =>
      add.templateDocument.name ==
        GERMANY_CONTRACT_ADDENDUM_TYPES.CHANGE_AMOUNT_HOURS && add.signedAt,
  )

  let newContract = contract
  const changeEndDateAddendums = addendums.filter(
    add =>
      [
        GERMANY_CONTRACT_ADDENDUM_TYPES.CONTRACT_EXTENSION,
        GERMANY_CONTRACT_ADDENDUM_TYPES.FUTURE_RESIGNATION,
        GERMANY_CONTRACT_ADDENDUM_TYPES.IMMEDIATE_RESIGNATION,
        GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_DOESNT_SEND_LETTER,
        GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_DOESNT_SHOW_UP,
        GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_LESS_THAN_6_MONTH,
        GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_MORE_THAN_6_MONTH,
      ].includes(add.templateDocument.name) && add.signedAt,
  )
  if (hoursPerWeekAddendums.length > 0) {
    const lastHoursPerWeekAddendum = hoursPerWeekAddendums.sort(
      (a, b) => b.id - a.id,
    )[0]
    newContract = {
      ...newContract,
      metadata: {
        ...contract.metadata,
        fields: {
          ...contract.metadata.fields,
          hoursPerWeek: lastHoursPerWeekAddendum.metadata.fields.hoursPerWeek,
        },
      },
    }
  }
  if (changeEndDateAddendums.length > 0) {
    const lastChangeEndDateAddendum = changeEndDateAddendums.sort(
      (a, b) => b.id - a.id,
    )[0]
    newContract = {
      ...newContract,
      endAt: lastChangeEndDateAddendum.endAt,
      status: getContractStatus(
        contract.startAt,
        lastChangeEndDateAddendum.endAt,
      ),
    }
  }
  return newContract
}

export const groupContracts = allContracts => {
  if (allContracts.length == 0) {
    return { active: [], archived: [] }
  }
  const mainContracts = allContracts.filter(
    contract => !contract.relatedContractId,
  )
  const groupedAddendumsByContractId = mainContracts.reduce((acc, contract) => {
    const relatedAddendums = allContracts.filter(
      c => c.relatedContractId == contract.id,
    )
    acc[contract.id] = relatedAddendums
    return acc
  }, {})

  const combinedData = mainContracts.map(mainContract => {
    return {
      contract: decorateContractWithNewValues({
        contract: mainContract,
        addendums: groupedAddendumsByContractId[mainContract.id],
      }),
      addendums: decorateAddsWithNewValue(
        groupedAddendumsByContractId[mainContract.id],
      ),
    }
  })

  const splitedContractForTabs = combinedData.reduce(
    (acc, groupedContract) => {
      if (
        groupedContract.contract.status &&
        groupedContract.contract.status ==
          GERMANY_CONTRACT_STATUSES.STATUS_TERMINATED
      ) {
        acc.archived.push(groupedContract)
      } else {
        acc.active.push(groupedContract)
      }
      return acc
    },
    { active: [], archived: [] },
  )
  return splitedContractForTabs
}

export const shouldShowContractorType = countryCode =>
  [COUNTRY_CODE.SPAIN, COUNTRY_CODE.GERMANY].includes(countryCode)

export const shouldShowShowBaType = countryCode =>
  [COUNTRY_CODE.POLAND].includes(countryCode)

export const shouldShowGermanyPaymentId = countryCode =>
  [COUNTRY_CODE.GERMANY].includes(countryCode)
